import { useState } from 'react';
import { DemoPopup } from '../components/forms/DemoPopup';
import { Button } from '../components/ui/button';
import { Calendar, CheckCircle2 } from 'lucide-react';

const benefits = [
  'See the platform in action with a live walkthrough',
  'Get answers to your specific questions',
  'Learn how Niyanta.io can solve your business challenges',
  'No commitment required - just information',
  'Personalized recommendations for your needs',
];

export const BookDemoPage = () => {
  const [showPopup, setShowPopup] = useState(false);

  return (
    <div className="min-h-screen pt-20" data-testid="book-demo-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="book-demo-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div>
                <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  Book a Demo
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white">
                  See Niyanta.io in Action
                </h1>
              </div>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Schedule a personalized demo with our team and discover how Niyanta.io can 
                transform your business operations. We'll show you exactly how our platform 
                can help you achieve your goals.
              </p>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li 
                    key={benefit} 
                    className={`flex items-start gap-3 animate-fade-in-up stagger-${index + 1}`}
                  >
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="animate-fade-in-up stagger-5">
                <DemoPopup
                  title="Book Your Demo"
                  formType="Book Demo Request"
                  trigger={
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-7 text-lg font-medium glow-primary glow-primary-hover btn-shine animate-pulse-glow"
                      data-testid="book-demo-main-btn"
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      Book Your Demo Now
                    </Button>
                  }
                />
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="relative animate-fade-in-up stagger-2">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="Demo Session"
                  className="w-full h-auto opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 glass rounded-xl p-4 animate-fade-in-up stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">30 min Demo</div>
                    <div className="text-sm text-muted-foreground">Free, no commitment</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="book-demo-expectations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
              What to Expect
            </h2>
            <p className="text-muted-foreground text-lg mt-4 max-w-2xl mx-auto">
              Our demo sessions are designed to give you a comprehensive understanding of how 
              Niyanta.io can help your business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-1" data-testid="expectation-discovery">
              <div className="text-4xl font-bold text-primary font-heading mb-4">01</div>
              <h3 className="text-xl font-semibold font-heading text-white mb-3">
                Discovery
              </h3>
              <p className="text-muted-foreground">
                We'll learn about your business, challenges, and goals to tailor the demo to your needs.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-2" data-testid="expectation-demonstration">
              <div className="text-4xl font-bold text-primary font-heading mb-4">02</div>
              <h3 className="text-xl font-semibold font-heading text-white mb-3">
                Demonstration
              </h3>
              <p className="text-muted-foreground">
                A live walkthrough of the platform features most relevant to your business needs.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-3" data-testid="expectation-qa">
              <div className="text-4xl font-bold text-primary font-heading mb-4">03</div>
              <h3 className="text-xl font-semibold font-heading text-white mb-3">
                Q&A Session
              </h3>
              <p className="text-muted-foreground">
                Get all your questions answered and discuss next steps if you're ready to proceed.
              </p>
            </div>
          </div>

          <div className="text-center mt-16 animate-fade-in-up stagger-4">
            <DemoPopup
              title="Book Your Demo"
              formType="Book Demo Request"
              trigger={
                <Button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                  data-testid="book-demo-cta-btn"
                >
                  Schedule Your Demo
                </Button>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookDemoPage;
